import { CheckCircle, Dot } from "lucide-react";
import { usePrices } from "@/api/cms";
import { usePuckEditorLocale } from "@/context/cms-puck/PuckEditorLocaleContext";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

type ConsultationPrice = {
  id: string;
  name?: Record<string, string>;
  description?: Record<string, string>;
  priceDescriptionFromStripe?: { price?: { unit_amount?: number | string } };
};

/** Valeur dans la langue d'édition, sinon la première langue renseignée. */
function localized(value: Record<string, string> | undefined, locale: string) {
  if (!value) return "";
  return value[locale] || Object.values(value).find(Boolean) || "";
}

/**
 * Aperçu éditeur du bloc `SubscriptionOffers` : une carte par forfait
 * d'abonnement (prix CONSULTATION Stripe), bouton non cliquable ici.
 * PARITÉ front : PuckSubscriptionOffers + AbonnementCard.
 */
export function SubscriptionOffersBlock() {
  const cmsNew = useCmsNew();
  const locale = usePuckEditorLocale() ?? "fr";
  const { data, isLoading } = usePrices();
  // L'API renvoie une chaîne (pas un tableau) quand l'instance est introuvable.
  const prices = (Array.isArray(data) ? data : []) as ConsultationPrice[];

  return (
    <div className="flex w-full flex-col items-center gap-16 px-5 py-8 text-center md:px-10">
      <div className="mx-auto mb-20 flex w-full max-w-screen-xl flex-col gap-5 md:flex-row">
        {isLoading ? null : prices.length === 0 ? (
          <p className="w-full text-sm opacity-60">
            {cmsNew(
              "cms.new.blocks.subscriptionOffers.empty",
              "Aucun forfait d'abonnement configuré dans Stripe.",
            )}
          </p>
        ) : (
          prices.map((price) => (
            <div
              key={price.id}
              className="cms-page-border-strong cms-page-surface flex w-full flex-col items-center gap-6 rounded-2xl border p-8 shadow-lg"
            >
              <div className="cms-page-border rounded-xl border p-1">
                <span className="cms-page-surface-elevated inline-flex items-center gap-2 rounded-md border px-2.5 py-0.5 text-sm font-medium">
                  <Dot size={8} />
                  {localized(price.name, locale)}
                </span>
              </div>
              <p className="text-5xl font-semibold">
                {Number(
                  price.priceDescriptionFromStripe?.price?.unit_amount ?? 0,
                ) / 100}{" "}
                €
              </p>
              <div className="cms-page-border cms-page-text-muted flex items-start gap-4 border-y py-8">
                <CheckCircle size={32} />
                <p className="text-left text-base font-normal">
                  {localized(price.description, locale)}
                </p>
              </div>
              {/* Bouton par défaut du front (noir), pas la couleur de bouton du thème. */}
              <span className="inline-flex h-9 w-full items-center justify-center whitespace-nowrap rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-zinc-50 shadow">
                {cmsNew(
                  "cms.new.blocks.subscriptionOffers.button",
                  "S'abonner",
                )}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
